import { useInfiniteQuery } from '@tanstack/react-query';
import { useAuth } from 'app/providers/AuthProvider';
import { requestJson } from 'features/agents/api';
import { useCurrentUserAccess } from 'shared/session/useCurrentUserRole';

export const ACTIVITY_REFRESH_MS = 30_000;
export const activityKeys = {
  all: ['agent-activity'],
  scope: (identity, organizationId, profileId, window = 'live') => ['agent-activity', identity, organizationId, profileId, window],
};

/** `live` is work active in the last few minutes; `recent` is everything in the past hour. The server applies both rules. */
export const activityWindow = (value) => (value === 'recent' ? 'recent' : 'live');

export const getAgentActivity = ({ signal, pageParam = 0, window = 'live' } = {}) => requestJson(
  `/api/agents/activity?limit=100&offset=${pageParam}&window=${activityWindow(window)}`,
  { signal },
);

function combinePages(data) {
  const first = data.pages[0];
  const last = data.pages[data.pages.length - 1];
  const agents = new Map();
  data.pages.forEach((page) => page.agents.forEach((agent) => agents.set(agent.agent_id, agent)));
  return {
    ...first,
    as_of: data.pages.map((page) => page.as_of).sort()[0],
    agents: [...agents.values()],
    has_more: last.has_more,
    freshness: {
      ...first.freshness,
      state: data.pages.some((page) => page.freshness?.state === 'degraded') ? 'degraded' : 'fresh',
      unavailable: [...new Set(data.pages.flatMap((page) => page.freshness?.unavailable || []))],
    },
  };
}

/** Monitoring permissions come from the server, independently of the chat roster. */
export function useAgentActivity({ window = 'live' } = {}) {
  const requested = activityWindow(window);
  const { user } = useAuth();
  const access = useCurrentUserAccess();
  const identity = user?.id || user?.uid;
  const organizationId = access.data?.organizationId;
  const profileId = access.data?.profileId;
  const query = useInfiniteQuery({
    queryKey: activityKeys.scope(identity, organizationId, profileId, requested),
    queryFn: ({ signal, pageParam }) => getAgentActivity({ signal, pageParam, window: requested }),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => lastPage.has_more ? lastPage.next_offset : undefined,
    select: combinePages,
    enabled: Boolean(identity && organizationId && profileId),
    staleTime: 0,
    gcTime: 0,
    retry: false,
    refetchInterval: ACTIVITY_REFRESH_MS,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: 'always',
  });
  return {
    ...query,
    isError: query.isError || access.isError,
    refetch: () => access.isError || !organizationId || !profileId ? access.refetch() : query.refetch(),
  };
}
